/**
 * IIT Bombay × IRCC register-interest page — the fixed copy, from the
 * owner's prototype (2026-10-12), with its slips fixed. Prices, the closing
 * date, the cap and the contact e-mail are NOT here: they come from
 * GET /api/v1/ip-intel/programme/iitb_ircc/config (admin-editable), and
 * IRCC_FALLBACK below only fills the page until that answers.
 */

export const IRCC_KEY = 'iitb_ircc';
export const IRCC_PATH = '/ip-intel-report/iit-bombay';
export const IRCC_CANONICAL = 'https://universitytech.io/ip-intel-report/iit-bombay';
export const IRCC_DRAFT_KEY = 'ipintel_ircc_draft';

export const IRCC_META = {
  title: 'IP Intelligence Report for IIT Bombay faculty | UniversityTech.io',
  description:
    'IIT Bombay faculty: register your interest in an IP Intelligence Report, a commercial assessment of your '
    + 'technology. IRCC covers half the cost. No payment when you register.',
};

/** What the page shows before the programme config arrives (the seed values). */
export const IRCC_FALLBACK = {
  share_label: '₹20,000 + GST',
  share_amount_label: '₹20,000',
  full_price_label: '₹40,000 + GST',
  tax_label: 'GST',
  share_minor: 2_000_000,
  currency: 'INR',
  tech_cap: 10,
  max_per_registration: 10,
  contact_email: 'info@universitytech.io',
  turnaround_days: 7,
};

/** The seven report modules, as the prototype words them (shorter than the main page's). */
export const IRCC_MODULES: { n: string; q: string; module: string }[] = [
  { n: '01', q: 'How big is the opportunity, really?', module: 'Market Sizing: Bottom-Up vs. Top-Down' },
  { n: '02', q: 'What should we ask for, and why?', module: 'Royalty Benchmarking & Deal Structure' },
  { n: '03', q: 'Where is it worth most, and when?', module: 'Technology Profiling & Use Cases' },
  { n: '04', q: 'Who solves this, and can we win?', module: 'Competitive Landscape' },
  { n: '05', q: 'Is the IP defensible, blocked, or open?', module: 'IP Landscape' },
  { n: '06', q: 'What does proof cost, and who pays?', module: 'Funding Pathway' },
  { n: '07', q: 'Licence, spin out, or partner: what next?', module: 'GTM Recommendation' },
];

/** VIT Vellore → Fermenta Biotech (owner supplied, 2026-10-12). */
export const IRCC_CASE_STUDY = {
  eyebrow: 'Case study · VIT Vellore',
  title: 'From report to an exclusive licence with a listed company',
  body:
    'VIT used an IP Intelligence Report from UniversityTech.io for its Vitamin B12 fortification technology for '
    + 'fermented foods. The technology has since been exclusively licensed to Fermenta Biotech, a publicly listed company.',
  link: 'https://businessupturn.com/finance/stock-market/fermenta-biotech-shares-jump-over-4-on-vitamin-b12-technology-deal-with-vit/',
  linkLabel: 'Read about the deal',
  facts: [
    { label: 'Technology', value: 'Natural Vitamin B12 fortification in fermented foods' },
    { label: 'Licensee', value: 'Fermenta Biotech (exclusive licence)' },
    { label: 'Announced', value: 'September 2026' },
  ],
};

export const IRCC_ABOUT =
  'UniversityTech.io is an IIT Bombay SINE-incubated technology transfer platform that connects university '
  + 'innovations with industry. Its IP Intelligence Reports help researchers find the right commercial path for '
  + 'their technology.';

/** TRL options for the form: the prototype's short labels. */
export const IRCC_TRLS: { n: number; label: string }[] = [
  { n: 1, label: 'Basic principles observed' },
  { n: 2, label: 'Concept formulated' },
  { n: 3, label: 'Proof of concept' },
  { n: 4, label: 'Validated in lab' },
  { n: 5, label: 'Validated in relevant environment' },
  { n: 6, label: 'Demonstrated in relevant environment' },
  { n: 7, label: 'Prototype in operational environment' },
  { n: 8, label: 'System complete and qualified' },
  { n: 9, label: 'Proven in operation' },
];

/** The four steps. `share` is the per-technology share label, `days` the turnaround. */
export const irccSteps = (share: string, days: number): { n: string; t: string; body: string }[] => [
  {
    n: '01', t: 'Register your interest',
    body: 'Share your details and the title and TRL of each technology. One report per technology. No payment at this stage.',
  },
  {
    n: '02', t: 'Pay your share to IRCC',
    body: 'IRCC team will get in touch with you with next steps.',
  },
  {
    n: '03', t: 'Confirm your technology',
    body: 'UniversityTech.io sends you your IRCC Tech Transfer listing to confirm or add context. Not listed yet? You\'ll get a short form instead.',
  },
  {
    n: '04', t: 'Report delivered',
    body: `Within ${days} days of your confirmation, the full report goes to you and to IRCC.`,
  },
];

export const irccFaqs = (share: string, days: number): { q: string; a: string }[] => [
  {
    q: 'Do I pay anything when I register?',
    a: 'No. Registering records your interest. IRCC will get in touch with you with next steps.',
  },
  {
    q: 'Can one report cover several technologies?',
    a: 'No. Each report covers one technology, so the market, royalty and IP analysis stays specific enough to negotiate on. Register each technology separately in the form.',
  },
  {
    q: 'My technology isn\'t on the IRCC Tech Transfer page. Can I still register?',
    a: 'Yes. If it\'s listed, UniversityTech.io works from that listing. If not, you\'ll receive a short form to share the technology details yourself.',
  },
  {
    q: 'How long does it take?',
    a: `${days === 7 ? 'Seven' : days} days from when you confirm your technology details.`,
  },
  {
    q: 'Is this a legal opinion?',
    a: 'No. The IP Landscape module is a commercial analysis of the patent terrain. It is useful for flagging risk and directing counsel, but it is not a substitute for a formal freedom-to-operate opinion.',
  },
];

/** "₹40,000 + GST" for n technologies at `shareMinor` (paise), as the server formats it. */
export const shareTotalLabel = (shareMinor: number, n: number, taxLabel: string): string => {
  const rupees = Math.round((shareMinor * Math.max(0, n)) / 100);
  return `₹${rupees.toLocaleString('en-IN')}${taxLabel ? ` + ${taxLabel}` : ''}`;
};

export const techWord = (n: number) => (n === 1 ? 'technology' : 'technologies');

/** Mirrors the server's check (ip_intel_programme._email_allowed): no domains
 *  = any address; otherwise the domain itself or any subdomain of it. */
export const emailDomainAllowed = (email: string, domains: string[]): boolean => {
  if (!domains.length) return true;
  const host = email.trim().toLowerCase().split('@').pop() || '';
  return domains.some(d => host === d || host.endsWith(`.${d}`));
};

/** "Use your @iitb.ac.in address." for a restricted form, '' when any address will do. */
export const emailDomainsHint = (domains: string[]): string => {
  if (!domains.length) return '';
  return domains.length === 1
    ? `Use your @${domains[0]} address.`
    : `Use an address at ${domains.map(d => `@${d}`).join(', ')}.`;
};

/** false only when the server says places are unlimited (no cap, no places
 *  count on the page); an older server that does not send it = limited. */
export const placesLimited = (p: { places_limited?: boolean } | null | undefined): boolean =>
  !p || p.places_limited !== false;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ordinal = (n: number): string => {
  const t = n % 100;
  if (t >= 11 && t <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] || 'th'}`;
};

/** "2nd Nov 2026" in India time (owner's wording, 2026-10-15), '' for none. */
export const closesOn = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'numeric', year: 'numeric', timeZone: 'Asia/Kolkata' }).formatToParts(d);
  } catch {
    parts = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'numeric', year: 'numeric' }).formatToParts(d);
  }
  const part = (t: string) => Number(parts.find(p => p.type === t)?.value || 0);
  // Our own three-letter months: some browsers write September as "Sept".
  const month = MONTHS[part('month') - 1];
  return part('day') && month ? `${ordinal(part('day'))} ${month} ${part('year')}` : '';
};
